import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {adminRequest, buildQuery, getReturnedObject} from "../../api/adminApi";

const pageInfoFrom = (data: any) => ({
  pageNumber: data?.pageNumber || 1,
  pageSize: data?.pageSize || 10,
  totalItems: data?.totalRecords || 0,
  totalPages: Math.ceil((data?.totalRecords || 0) / (data?.pageSize || 10)),
});

const initialState = {
  report: null as any[] | null,
  reportSummary: null as any,
  liquidationRequests: null as any[] | null,
  selectedLiquidationRequest: null as any,
  refundRequests: null as any[] | null,
  selectedRefundRequest: null as any,
  loading: false,
  error: undefined as string | undefined,
  pageInfo: {pageNumber: 1, pageSize: 10, totalItems: 0, totalPages: 0},
};

/**
 * The report endpoint filters by status, type and a date window server-side, so every tab and
 * control on the page is a real query rather than a slice of the page already fetched.
 */
export const getPaymentLinkReport = createAsyncThunk<any, {
  token: string;
  searchParam?: string;
  pageNumber?: number;
  status?: number | string;
  type?: number | string;
  fromDate?: string;
  toDate?: string;
}>(
  "paymentLinks/report",
  async (data) =>
    adminRequest(`/payment-links/report${buildQuery({
      searchParam: data.searchParam,
      pageNumber: data.pageNumber || 1,
      pageSize: 10,
      status: data.status,
      type: data.type,
      fromDate: data.fromDate,
      toDate: data.toDate,
    })}`, {token: data.token})
);

export const repushPaymentLinkPayment = createAsyncThunk<any, {token: string; paymentId: string}>(
  "paymentLinks/repush",
  async ({token, paymentId}) =>
    adminRequest(`/payment-links/payments/${paymentId}/repush`, {method: "POST", token})
);

export const refundFailedPaymentLinkToBalance = createAsyncThunk<any, {token: string; paymentId: string; reason?: string}>(
  "paymentLinks/refundFailedToBalance",
  async (data: {token: string; paymentId: string; reason?: string}) =>
    adminRequest(`/payment-links/payments/${data.paymentId}/refund-to-balance`, {
      method: "POST",
      token: data.token,
      body: JSON.stringify({reason: data.reason}),
    })
);

export const getPaymentLinkLiquidationRequests = createAsyncThunk<any, {token: string; searchParam?: string; status?: string; pageNumber?: number}>(
  "paymentLinks/liquidationRequests",
  async (data: {token: string; searchParam?: string; status?: string; pageNumber?: number}) =>
    adminRequest(`/payment-links/liquidation-requests${buildQuery({
      searchParam: data.searchParam,
      status: data.status,
      pageNumber: data.pageNumber || 1,
      pageSize: 10,
    })}`, {token: data.token})
);

export const getPaymentLinkLiquidationRequestById = createAsyncThunk<any, {token: string; requestId: string}>(
  "paymentLinks/liquidationRequestById",
  async (data: {token: string; requestId: string}) =>
    adminRequest(`/payment-links/liquidation-requests/${data.requestId}`, {token: data.token})
);

export const approvePaymentLinkLiquidationRequest = createAsyncThunk<any, {token: string; requestId: string; isManualTransfer?: boolean; externalReference?: string; paymentProofUrl?: string; narration?: string}>(
  "paymentLinks/approveLiquidationRequest",
  async (data: {token: string; requestId: string; isManualTransfer?: boolean; externalReference?: string; paymentProofUrl?: string; narration?: string}) =>
    adminRequest(`/payment-links/liquidation-requests/${data.requestId}/approve`, {
      method: "POST",
      token: data.token,
      body: JSON.stringify({
        isManualTransfer: data.isManualTransfer === true,
        externalReference: data.externalReference,
        paymentProofUrl: data.paymentProofUrl,
        narration: data.narration,
      }),
    })
);

export const rejectPaymentLinkLiquidationRequest = createAsyncThunk<any, {token: string; requestId: string; reason: string}>(
  "paymentLinks/rejectLiquidationRequest",
  async (data: {token: string; requestId: string; reason: string}) =>
    adminRequest(`/payment-links/liquidation-requests/${data.requestId}/reject`, {
      method: "POST",
      token: data.token,
      body: JSON.stringify({reason: data.reason}),
    })
);

export const refundStalePaymentLinkLiquidationRequest = createAsyncThunk<any, {token: string; requestId: string; reason?: string}>(
  "paymentLinks/refundStaleLiquidationRequest",
  async (data: {token: string; requestId: string; reason?: string}) =>
    adminRequest(`/payment-links/liquidation-requests/${data.requestId}/refund`, {
      method: "POST",
      token: data.token,
      body: JSON.stringify({reason: data.reason}),
    })
);

export const getPaymentLinkRefundRequests = createAsyncThunk<any, {token: string; searchParam?: string; pageNumber?: number}>(
  "paymentLinks/refundRequests",
  async (data: {token: string; searchParam?: string; pageNumber?: number}) =>
    adminRequest(`/payment-links/refund-requests${buildQuery({
      searchParam: data.searchParam,
      pageNumber: data.pageNumber || 1,
      pageSize: 10,
    })}`, {token: data.token})
);

export const getPaymentLinkRefundRequestById = createAsyncThunk<any, {token: string; requestId: string}>(
  "paymentLinks/refundRequestById",
  async (data: {token: string; requestId: string}) =>
    adminRequest(`/payment-links/refund-requests/${data.requestId}`, {token: data.token})
);

export const approvePaymentLinkRefundRequest = createAsyncThunk<any, {token: string; requestId: string; externalReference?: string; paymentProofUrl?: string; narration?: string}>(
  "paymentLinks/approveRefundRequest",
  async (data: {token: string; requestId: string; externalReference?: string; paymentProofUrl?: string; narration?: string}) =>
    adminRequest(`/payment-links/refund-requests/${data.requestId}/approve`, {
      method: "POST",
      token: data.token,
      body: JSON.stringify({
        externalReference: data.externalReference,
        paymentProofUrl: data.paymentProofUrl,
        narration: data.narration,
      }),
    })
);

export const rejectPaymentLinkRefundRequest = createAsyncThunk<any, {token: string; requestId: string; reason: string}>(
  "paymentLinks/rejectRefundRequest",
  async (data: {token: string; requestId: string; reason: string}) =>
    adminRequest(`/payment-links/refund-requests/${data.requestId}/reject`, {
      method: "POST",
      token: data.token,
      body: JSON.stringify({reason: data.reason}),
    })
);

const paymentLinkSlice = createSlice({
  name: "paymentLinks",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getPaymentLinkReport.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(getPaymentLinkReport.fulfilled, (state, action) => {
        const report = getReturnedObject(action.payload);
        state.loading = false;
        state.report = (report?.payments || []).map((item: any) => ({...item, key: item.paymentId}));
        state.reportSummary = report;
        state.pageInfo = pageInfoFrom(report);
      })
      .addCase(getPaymentLinkLiquidationRequests.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(getPaymentLinkLiquidationRequests.fulfilled, (state, action) => {
        const result = getReturnedObject(action.payload);
        const requests = result?.requests || result?.liquidationRequests || result?.items || result?.data || [];
        state.loading = false;
        state.liquidationRequests = requests.map((item: any) => ({...item, key: item.id}));
        state.pageInfo = pageInfoFrom(result);
      })
      .addCase(getPaymentLinkLiquidationRequestById.pending, (state) => {
        state.loading = true;
        state.error = undefined;
        state.selectedLiquidationRequest = null;
      })
      .addCase(getPaymentLinkLiquidationRequestById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedLiquidationRequest = getReturnedObject(action.payload);
      })
      .addCase(approvePaymentLinkLiquidationRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(rejectPaymentLinkLiquidationRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(refundStalePaymentLinkLiquidationRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(getPaymentLinkRefundRequests.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(getPaymentLinkRefundRequests.fulfilled, (state, action) => {
        const result = getReturnedObject(action.payload);
        const requests = result?.requests || result?.items || result?.data || [];
        state.loading = false;
        state.refundRequests = requests.map((item: any) => ({...item, key: item.id}));
        state.pageInfo = pageInfoFrom(result);
      })
      .addCase(getPaymentLinkRefundRequestById.pending, (state) => {
        state.loading = true;
        state.error = undefined;
        state.selectedRefundRequest = null;
      })
      .addCase(getPaymentLinkRefundRequestById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedRefundRequest = getReturnedObject(action.payload);
      })
      .addCase(approvePaymentLinkRefundRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(rejectPaymentLinkRefundRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addMatcher(
        (action) => action.type.startsWith("paymentLinks/") && action.type.endsWith("/rejected"),
        (state, action: any) => {
          state.loading = false;
          state.error = action.error?.message;
        }
      );
  },
});

export default paymentLinkSlice.reducer;
